import { useMemo } from 'react';
import {
  PATIENT_TYPE_OPTIONS,
  PATIENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  REFERRED_BY_TYPE_OPTIONS,
  CONVERSION_STATUS_OPTIONS,
} from '@src/pages/patients/constants';
import { getPhoneFieldConfig, formatPhoneNumberWithPrefix } from '@src/utils/phoneUtils';
import {
  formatOptionalPersonNameField,
  formatPersonNameField,
} from '@src/utils/nameUtils';
import dayjs from 'dayjs';
import { FormField } from '@src/components/SmartDataTable/types';

export const usePatientFormFields = (): FormField[] => {
  return useMemo(
    () => [
      // Row 1: First Name (Left) | Last Name (Right)
      {
        name: 'first_name',
        label: 'First Name',
        type: 'text' as const,
        required: true,
        placeholder: "Enter patient's first name",
        layout: { span: 12 },
        props: {
          size: 'large',
          maxLength: 30,
          resourceAutoComplete: 'given-name',
        },
        tooltip: "Enter the patient's first name",
        transform: {
          output: (value: any) => formatPersonNameField(value),
        },
      },
      {
        name: 'last_name',
        label: 'Last Name',
        type: 'text' as const,
        required: false,
        placeholder: "Enter patient's last name",
        layout: { span: 12 },
        props: {
          size: 'large',
          maxLength: 30,
          resourceAutoComplete: 'family-name',
        },
        tooltip: "Enter the patient's last name",
        transform: {
          output: (value: any) => formatPersonNameField(value),
        },
      },

      // Row 2: Mobile Number (Left) | Gender (Right)
      {
        name: 'mobile_no',
        label: 'Mobile Number',
        ...getPhoneFieldConfig(true, 'Enter 10-digit mobile number'),
        layout: { span: 12 },
        tooltip: 'Enter 10-digit mobile number (without country code)',
        transform: {
          input: (value: any) => {
            // Clean input value - remove any non-digits
            if (!value || value === null || value === undefined) {
              return value;
            }
            if (typeof value === 'string') {
              return value.replace(/\D/g, '');
            }
            return value;
          },
          output: (value: any) => {
            // Add '91' prefix for backend submission
            if (!value || value === null || value === undefined || value === '') {
              return value;
            }
            // Use formatPhoneNumberWithPrefix to ensure proper formatting
            const formatted = formatPhoneNumberWithPrefix(value);
            return formatted === '' ? null : formatted;
          },
        },
      },
      {
        name: 'secondary_mobile_number',
        label: 'Secondary Mobile Number',
        ...getPhoneFieldConfig(false, 'Enter 10-digit secondary mobile number'),
        layout: { span: 12 },
        required: false,
        tooltip:
          'Enter 10-digit secondary mobile number (without country code)',
        defaultValue: null,
        onChange: (value: any, form: any) => {
          // Convert empty string or undefined to null for database
          // This ensures the form state always has null instead of empty string
          if (!value || value === '' || value === undefined || (typeof value === 'string' && value.trim() === '')) {
            form.setFieldsValue({ secondary_mobile_number: null });
          }
        },
        transform: {
          input: (value: any) => {
            // Clean the input value - remove any non-digits, or return null if empty
            if (!value || value === null || value === undefined) {
              return null;
            }
            if (typeof value === 'string') {
              const cleaned = value.replace(/\D/g, '');
              return cleaned === '' ? null : cleaned;
            }
            return value;
          },
          output: (value: any) => {
            // Convert empty strings to null for database, or add '91' prefix if value exists
            if (!value || value === null || value === undefined || value === '') {
              return null;
            }
            // Use formatPhoneNumberWithPrefix to ensure proper formatting with '91' prefix
            const formatted = formatPhoneNumberWithPrefix(value);
            return formatted === '' ? null : formatted;
          },
        },
      },
      // Row 3: Gender (Left) | Birth Date (Right)
      {
        name: 'gender',
        label: 'Gender',
        type: 'select' as const,
        required: false,
        placeholder: 'Select gender',
        layout: { span: 12 },
        options: [...GENDER_OPTIONS],
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
        },
        tooltip: 'Select patient gender',
      },
      {
        name: 'birth_date',
        label: 'Birth Date',
        type: 'date' as const,
        required: false,
        placeholder: 'Select birth date',
        layout: { span: 12 },
        props: {
          size: 'large',
          format: 'YYYY-MM-DD',
          disabledDate: (current: any) => {
            return current && current > dayjs().endOf('day');
          },
          placeholder: 'Select birth date',
        },
        onChange: (value: any, form: any) => {
          if (value && form) {
            // Calculate age from birth date
            const age = dayjs().diff(dayjs(value), 'year');
            form.setFieldsValue({ age });
            form.validateFields(['age']);
          } else if (!value && form) {
            // Clear age when birth date is cleared
            form.setFieldsValue({ age: null });
          }
        },
        tooltip: 'Select patient birth date (age will be auto-calculated)',
      },

      // Row 4: Age (Left) | Address (Right)
      {
        name: 'age',
        label: 'Age',
        type: 'number' as const,
        required: true,
        placeholder: 'Enter age or auto-calculated from birth date',
        layout: { span: 12 },
        props: {
          size: 'large',
          min: 0,
          max: 150,
        },
        onChange: (value: any, form: any) => {
          // Clear birth date when age is manually entered
          if (value && form) {
            const currentBirthDate = form.getFieldValue('birth_date');
            if (currentBirthDate) {
              form.setFieldsValue({ birth_date: null });
              form.validateFields(['birth_date']);
            }
          }
        },
        tooltip: 'Age is auto-calculated from birth date, or enter manually',
      },
      {
        name: 'address',
        label: 'Address',
        type: 'textarea' as const,
        required: false,
        placeholder: 'Enter patient address',
        layout: { span: 12 },
        props: {
          size: 'large',
          maxLength: 100,
          rows: 2,
          autoSize: { minRows: 2, maxRows: 3 },
        },
        tooltip: 'Enter patient address',
      },

      // Row 5: Type (Left) | Patient Status (Right)
      {
        name: 'type',
        label: 'Patient Type',
        type: 'select' as const,
        required: true,
        placeholder: 'Select patient type(s)',
        layout: { span: 12 },
        options: [...PATIENT_TYPE_OPTIONS],
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
          mode: 'multiple',
          allowClear: true,
          maxTagCount: 'responsive',
          maxTagTextLength: 10,
        },
        tooltip: 'Select one or more patient types',
      },
      {
        name: 'patient_status',
        label: 'Patient Status',
        type: 'select' as const,
        required: true,
        placeholder: 'Select patient status',
        layout: { span: 12 },
        options: [...PATIENT_STATUS_OPTIONS],
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
        },
        tooltip: 'Select the status of the patient',
      },

      // Row 6: Referred By Name (Left) | Referred By Type (Right)
      {
        name: 'referred_by_name',
        label: 'Referred By Name',
        type: 'text' as const,
        required: false,
        placeholder: 'Enter referrer name',
        layout: { span: 12 },
        props: {
          size: 'large',
          maxLength: 30,
        },
        tooltip: 'Enter the name of person who referred this patient',
        defaultValue: null,
        onChange: (value: any, form: any) => {
          // Convert empty string to null for database
          if (value === '') {
            form.setFieldsValue({ referred_by_name: null });
          }
        },
        transform: {
          output: (value: any) => formatOptionalPersonNameField(value),
        },
      },
      {
        name: 'referred_by_type',
        label: 'Referred By Type',
        type: 'select' as const,
        required: false,
        placeholder: 'Select referrer type',
        layout: { span: 12 },
        options: [...REFERRED_BY_TYPE_OPTIONS],
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
          allowClear: true,
        },
        tooltip: 'Select the type of referrer',
        defaultValue: null,
        onChange: (value: any, form: any) => {
          // Convert undefined/empty to null for database and clear referred_by_name when type is cleared
          if (value === undefined || value === '') {
            form.setFieldsValue({ referred_by_type: null });
            form.setFieldsValue({ referred_by_name: null });
          }
        },
      },

      // Row 7: Follow Up Date (Left) | Conversion Status (Right)
      {
        name: 'follow_up_date',
        label: 'Follow Up Date & Time',
        type: 'datetime' as const,
        required: false,
        placeholder: 'When should you follow up?',
        layout: { span: 12 },
        defaultValue: (() => {
          const now = dayjs();
          const minutes = now.minute();
          const roundedMinutes = Math.round(minutes / 15) * 15;
          let defaultTime = now.minute(roundedMinutes).second(0).millisecond(0);

          if (defaultTime.hour() < 9) {
            defaultTime = defaultTime.hour(9).minute(0);
          } else if (defaultTime.hour() >= 20) {
            defaultTime = defaultTime.add(1, 'day').hour(9).minute(0);
          }

          return defaultTime;
        })(),
        props: {
          size: 'large',
          showTime: true,
          showShortcuts: true,
          format: 'YYYY-MM-DD hh:mm A',
          showNow: true,
          showToday: true,
          minuteStep: 15,
          use12Hours: true,
          disabledDate: (current: any) => {
            return current && current < dayjs().startOf('day');
          },
          placeholder: 'Select date and time',
        },
        tooltip: 'Set a reminder for when to follow up with this patient',
      },
      {
        name: 'conversion_status',
        label: 'Conversion Status',
        type: 'select' as const,
        required: false,
        placeholder: 'Select conversion status',
        layout: { span: 12 },
        options: [...CONVERSION_STATUS_OPTIONS],
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
        },
        tooltip: 'Select the conversion status of the patient',
        defaultValue: null,
        onChange: (value: any, form: any) => {
          // Convert undefined/empty to null for database and clear referred_by_name when type is cleared
          if (value === undefined || value === '') {
            form.setFieldsValue({ referred_by_type: null });
            form.setFieldsValue({ referred_by_name: null });
          }
        },
      },

      // Row 8: Notes (Full Width)
      {
        name: 'notes',
        label: 'Notes',
        type: 'textarea' as const,
        required: false,
        placeholder: 'Enter any additional notes about the patient',
        layout: { span: 24 },
        props: {
          size: 'large',
          showCount: true,
          maxLength: 100,
          rows: 3,
          autoSize: { minRows: 3, maxRows: 4 },
        },
        tooltip: 'Enter any additional notes or comments about the patient',
      },
    ],
    []
  );
};
